import { UnauthorizedError } from '@/errors'

import {
  DeliverySettings,
  DeliverySettingsResource,
  DeliverySettingsResourcePayload
} from '@/models'

import axios, { withToken } from '../axios'

import {
  generateCreate,
  generateDelete,
  generateUpdate
} from '../crudGenerator'

/**
 * Converts DeliverySettingsResource to DeliverySettings.
 *
 * @param resource Reponse data to be converted.
 */
export function parseDeliverySettingsResource (resource: DeliverySettingsResource): DeliverySettings {
  return {
    id: resource.id,
    businessUnitId: resource.shopId, // Renaming
    phone: resource.phone,
    slug: resource.slug,
    isActive: resource.active, // Renaming
    fee: resource.fee
  }
}

const url = '/menu'
const entity = 'delivery-settings'

export default {
  /**
   * Creates a new DeliverySettings.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param payload Data required to create it.
   */
  createDeliverySettings: generateCreate<DeliverySettings, DeliverySettingsResource, DeliverySettingsResourcePayload>(url, entity, parseDeliverySettingsResource),

  /**
   * Deletes a DeliverySettings.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param id DeliverySettings id to be deleted.
   */
  deleteDeliverySettings: generateDelete(url, entity),

  /**
   * Gets a DeliverySettings.
   *
   * @param tokenV2 Valid token with an AccountId.
   */
  async fetchDeliverySettings (tokenV2: string, accountId: number, shopId: number) {
    try {
      const response = await axios.get<DeliverySettingsResource>(
        `${url}/accounts/${accountId}/shops/${shopId}/${entity}`,
        withToken(tokenV2)
      )

      return parseDeliverySettingsResource(response.data)
    } catch (error) {
      const status = error?.response?.status

      if (status === 404) {
        return null
      } else if (status === 401) {
        return Promise.reject(new UnauthorizedError())
      } else {
        return Promise.reject(error)
      }
    }
  },

  /**
   * Updates an existing DeliverySettings.
   *
   * @param tokenV2 Valid token with an AccountId.
   * @param id DeliverySettings id to be updated.
   * @param payload Data required to create it.
   */
  updateDeliverySettings: generateUpdate<DeliverySettings, DeliverySettingsResource, DeliverySettingsResourcePayload>(url, entity, parseDeliverySettingsResource),

  /**
   * Checks if a slug is available.
   *
   * @param slug Slug to be verified.
   */
  async verifySlug (slug: string): Promise<boolean> {
    const response = await axios.get<{ valid: boolean }>(
      `${url}/verify`,
      {
        params: { slug }
      }
    )

    return response.data.valid
  }
}
